const orderState = { values: new Map(), revision: 0, savedRevision: 0, pending: null, timer: null, url: '' };
const CONFIRM_KEY = 'Gift card message confirmed';
const DATE_KEY = 'Delivery date';
const WINDOW_KEY = 'Delivery window';
const MORNING_WINDOW = 'Morning (9 am – 12 pm)';
const DATE_ERROR_TEXT = 'Please choose a delivery date.';

function announceOrderSave(state) {
  document.querySelectorAll('polianthes-cart-details').forEach((element) => {
    const status = element.querySelector('[data-order-save-status]');
    if (status) status.textContent = element.dataset[state] || '';
  });
}

function queueSave() {
  orderState.revision += 1;
  clearTimeout(orderState.timer);
  orderState.timer = setTimeout(() => saveOrderDetails().catch(() => {}), 250);
}

async function saveOrderDetails() {
  clearTimeout(orderState.timer);
  if (orderState.pending) await orderState.pending;
  if (orderState.savedRevision === orderState.revision) return;
  const revision = orderState.revision;
  const attributes = Object.fromEntries(orderState.values);
  announceOrderSave('saving');
  orderState.pending = fetch(`${orderState.url || '/cart'}/update.js`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes })
  }).then(async (response) => {
    if (!response.ok) throw new Error('Cart details were not saved');
    const result = await response.json();
    if (result.errors) throw new Error('Cart details were not saved');
    orderState.savedRevision = revision;
  });
  try {
    await orderState.pending;
    announceOrderSave('saved');
  } catch (error) {
    announceOrderSave('saveError');
    throw error;
  } finally {
    orderState.pending = null;
  }
  if (orderState.savedRevision !== orderState.revision) await saveOrderDetails();
}

/** Returns the first cart-details element whose card message is filled in but not confirmed. */
function findUnconfirmedCardMessage() {
  for (const element of document.querySelectorAll('polianthes-cart-details')) {
    const message = element.querySelector('[data-card-message]');
    const confirm = element.querySelector('[data-card-confirm]');
    if (message && confirm && message.value.trim() && !confirm.checked) return element;
  }
  return null;
}

class PolianthesCartDetails extends HTMLElement {
  #controller;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    orderState.url = this.dataset.cartUrl;
    this.querySelectorAll('[data-order-attribute]').forEach((field) => {
      const key = field.dataset.orderAttribute;
      if (orderState.values.has(key)) field.value = orderState.values.get(key);
      else orderState.values.set(key, field.value);
    });
    const confirm = this.querySelector('[data-card-confirm]');
    if (confirm) {
      if (orderState.values.has(CONFIRM_KEY)) confirm.checked = orderState.values.get(CONFIRM_KEY) === 'Yes';
      else orderState.values.set(CONFIRM_KEY, confirm.checked ? 'Yes' : '');
    }
    this.#syncCardConfirm();
    this.addEventListener('input', (event) => {
      const field = event.target;
      if (!field.matches('[data-order-attribute]')) return;
      orderState.values.set(field.dataset.orderAttribute, field.value);
      if (field.matches('[data-card-message]')) this.#syncCardConfirm();
      queueSave();
    }, { signal });
    confirm?.addEventListener('change', () => {
      orderState.values.set(CONFIRM_KEY, confirm.checked ? 'Yes' : '');
      this.#syncCardConfirm();
      queueSave();
    }, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /** Show the confirmation tick only when there is a message; clear it when the message is emptied. */
  #syncCardConfirm() {
    const message = this.querySelector('[data-card-message]');
    const confirm = this.querySelector('[data-card-confirm]');
    const wrapper = this.querySelector('[data-card-confirm-wrapper]');
    const error = this.querySelector('[data-card-confirm-error]');
    if (!message || !confirm || !wrapper) return;
    const hasText = message.value.trim().length > 0;
    wrapper.hidden = !hasText;
    if (!hasText && confirm.checked) {
      confirm.checked = false;
      orderState.values.set(CONFIRM_KEY, '');
    }
    if (error && (confirm.checked || !hasText)) error.hidden = true;
  }

  /** Called before checkout when the message is not confirmed. */
  showConfirmError() {
    const details = this.querySelector('[data-card-details]');
    if (details) details.open = true;
    const error = this.querySelector('[data-card-confirm-error]');
    if (error) error.hidden = false;
    const confirm = this.querySelector('[data-card-confirm]');
    confirm?.closest('label')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    confirm?.focus({ preventScroll: true });
  }
}

if (!customElements.get('polianthes-cart-details')) customElements.define('polianthes-cart-details', PolianthesCartDetails);

/* ---------- Delivery date and time (replaces the Pickeasy app) ---------- */

/** Today's date (YYYY-MM-DD) and hour in the given time zone. */
function nowInZone(timeZone) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' })
      .formatToParts(new Date())
      .map((part) => [part.type, part.value])
  );
  return { ymd: `${parts.year}-${parts.month}-${parts.day}`, hour: Number(parts.hour) % 24 };
}

function addDays(ymd, days) {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

function formatYmd(ymd, options) {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { timeZone: 'UTC', ...options });
}

class PolianthesDeliveryDate extends HTMLElement {
  #controller;
  #timer;
  #observer;
  #renderQueued = false;
  #scrolled = false;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    try {
      this.closedDates = new Set(JSON.parse(this.dataset.closedDates || '[]'));
    } catch (error) {
      this.closedDates = new Set();
    }
    this.daysAhead = Number(this.dataset.daysAhead) || 60;
    this.cutoffHour = Number(this.dataset.cutoffHour) || 12;
    this.timeZone = this.dataset.timeZone || 'America/Toronto';
    if (!orderState.values.has(DATE_KEY)) orderState.values.set(DATE_KEY, this.dataset.initialDate || '');
    if (!orderState.values.has(WINDOW_KEY)) orderState.values.set(WINDOW_KEY, this.dataset.initialWindow || '');

    this.addEventListener('click', (event) => {
      const day = event.target.closest('[data-date]');
      if (day && !day.disabled) {
        this.#selectDate(day.dataset.date);
        return;
      }
      const windowButton = event.target.closest('[data-window]');
      if (windowButton && !windowButton.disabled) this.#selectWindow(windowButton.dataset.window);
    }, { signal });

    // The cart re-renders this markup from the server (e.g. after a quantity change):
    // redraw the dates and restore the customer's choice whenever that happens.
    this.#observer?.disconnect();
    this.#observer = new MutationObserver(() => this.#queueRender());
    this.#observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-checked', 'data-key'] });

    this.#render();
    // Re-apply the rules every minute, so the 12 pm cut-off applies even if the page stays open.
    clearInterval(this.#timer);
    this.#timer = setInterval(() => this.#render(), 60000);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    clearInterval(this.#timer);
  }

  get selectedDate() {
    return (orderState.values.get(DATE_KEY) || '').slice(0, 10);
  }

  get selectedWindow() {
    return orderState.values.get(WINDOW_KEY) || '';
  }

  #queueRender() {
    if (this.#renderQueued) return;
    this.#renderQueued = true;
    setTimeout(() => {
      this.#renderQueued = false;
      this.#render();
    }, 0);
  }

  #dates() {
    const today = nowInZone(this.timeZone).ymd;
    const dates = [];
    for (let i = 1; i <= this.daysAhead; i += 1) {
      const ymd = addDays(today, i);
      dates.push({ ymd, tomorrow: i === 1, closed: this.closedDates.has(ymd) });
    }
    return dates;
  }

  #isOpen(ymd) {
    return this.#dates().some((date) => date.ymd === ymd && !date.closed);
  }

  /** Morning delivery tomorrow is only possible for orders placed before the cut-off hour. */
  #morningAllowed(ymd) {
    const now = nowInZone(this.timeZone);
    return !(ymd === addDays(now.ymd, 1) && now.hour >= this.cutoffHour);
  }

  #selectDate(ymd) {
    orderState.values.set(DATE_KEY, `${ymd} (${formatYmd(ymd, { weekday: 'long' })})`);
    if (this.selectedWindow === MORNING_WINDOW && !this.#morningAllowed(ymd)) orderState.values.set(WINDOW_KEY, '');
    this.#hideError();
    this.#render();
    queueSave();
  }

  #selectWindow(value) {
    orderState.values.set(WINDOW_KEY, value);
    this.#hideError();
    this.#render();
    queueSave();
  }

  #render() {
    // Drop a saved choice that is no longer possible (date passed, closed, or morning after the cut-off).
    let changed = false;
    if (this.selectedDate && !this.#isOpen(this.selectedDate)) {
      orderState.values.set(DATE_KEY, '');
      changed = true;
    }
    if (this.selectedWindow === MORNING_WINDOW && this.selectedDate && !this.#morningAllowed(this.selectedDate)) {
      orderState.values.set(WINDOW_KEY, '');
      changed = true;
    }
    if (changed) queueSave();

    const selected = this.selectedDate;
    const daysBox = this.querySelector('[data-days]');
    if (daysBox) {
      const dates = this.#dates();
      const key = `${dates[0]?.ymd}|${[...this.closedDates].join(',')}`;
      if (daysBox.dataset.key !== key || !daysBox.querySelector('[data-date]')) {
        daysBox.innerHTML = dates
          .map((date) => {
            const label = formatYmd(date.ymd, { weekday: 'long', month: 'long', day: 'numeric' }) + (date.closed ? ', closed' : '');
            return `<button type="button" class="polianthes-delivery__day" role="radio" aria-checked="false" aria-label="${label}" data-date="${date.ymd}"${date.closed ? ' disabled' : ''}>`
              + `<span class="polianthes-delivery__day-week">${date.tomorrow ? 'Tomorrow' : formatYmd(date.ymd, { weekday: 'short' })}</span>`
              + `<span class="polianthes-delivery__day-num">${formatYmd(date.ymd, { day: 'numeric' })}</span>`
              + `<span class="polianthes-delivery__day-month">${formatYmd(date.ymd, { month: 'short' })}</span></button>`;
          })
          .join('');
        daysBox.dataset.key = key;
        this.#scrolled = false;
      }
      daysBox.querySelectorAll('[data-date]').forEach((button) => {
        const checked = String(button.dataset.date === selected);
        if (button.getAttribute('aria-checked') !== checked) button.setAttribute('aria-checked', checked);
      });
      const selectedButton = daysBox.querySelector('[aria-checked="true"]');
      if (selectedButton && !this.#scrolled) {
        daysBox.scrollLeft = Math.max(0, selectedButton.offsetLeft - daysBox.offsetLeft - 8);
        this.#scrolled = true;
      }
    }

    this.querySelectorAll('[data-window]').forEach((button) => {
      const value = button.dataset.window;
      const disabled = value === MORNING_WINDOW && Boolean(selected) && !this.#morningAllowed(selected);
      if (button.disabled !== disabled) button.disabled = disabled;
      const checked = String(value === this.selectedWindow);
      if (button.getAttribute('aria-checked') !== checked) button.setAttribute('aria-checked', checked);
    });
  }

  /** Returns a message when the delivery choice is incomplete or no longer possible, else ''. */
  validate() {
    this.#render();
    if (!this.selectedDate) return DATE_ERROR_TEXT;
    if (!this.selectedWindow) return 'Please choose a delivery time.';
    return '';
  }

  showError(message) {
    const error = this.querySelector('[data-delivery-error]');
    if (error) {
      error.textContent = message;
      error.hidden = false;
    }
    this.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  #hideError() {
    const error = this.querySelector('[data-delivery-error]');
    if (error) error.hidden = true;
  }
}

if (!customElements.get('polianthes-delivery-date')) customElements.define('polianthes-delivery-date', PolianthesDeliveryDate);

/*
 * If the Pickeasy app is still switched on, its widget sits just before the checkout button:
 * keep our gift details after it and use plain wording for its error.
 */
function arrangeCart() {
  const widget = document.querySelector('.cart__ctas pickeasy-app-widget');
  const details = document.querySelector('polianthes-cart-details');
  if (widget && details && widget.nextElementSibling !== details) widget.after(details);
  document.querySelectorAll('pickeasy-app-widget *').forEach((element) => {
    if (element.children.length === 0 && /select an order slot/i.test(element.textContent || '')) {
      element.textContent = DATE_ERROR_TEXT;
    }
  });
}

if (document.querySelector('pickeasy-app-widget') && document.querySelector('polianthes-cart-details')) {
  let queued = false;
  const observer = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    setTimeout(() => {
      queued = false;
      arrangeCart();
    }, 30);
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  arrangeCart();
}

document.addEventListener('click', async (event) => {
  const button = event.target.closest?.('button[name="checkout"]');
  if (!button || !document.querySelector('polianthes-cart-details, polianthes-delivery-date')) return;
  const delivery = document.querySelector('polianthes-delivery-date');
  const deliveryProblem = delivery?.validate();
  if (deliveryProblem) {
    event.preventDefault();
    event.stopImmediatePropagation();
    delivery.showError(deliveryProblem);
    return;
  }
  const unconfirmed = findUnconfirmedCardMessage();
  if (unconfirmed) {
    event.preventDefault();
    event.stopImmediatePropagation();
    unconfirmed.showConfirmError();
    return;
  }
  if (orderState.revision === orderState.savedRevision && !orderState.pending) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  try {
    await saveOrderDetails();
    if (button.isConnected && !button.disabled) button.click();
  } catch (error) {
    document.querySelector('[data-order-save-status]')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
}, true);

/* Designer's Choice opens on the "Seasonal Mix" mood (the florist chooses) unless a variant is in the URL. */
const url = new URL(window.location.href);
if (!url.searchParams.has('variant') && !url.searchParams.has('option_values') && !window.Shopify?.designMode) {
  customElements.whenDefined('variant-picker').then(() => {
    const picker = document.querySelector('variant-picker[data-product-handle="designer-s-choice"][data-template-product-match="true"]');
    const choice = picker?.querySelector(
      'input[data-option-name="Colour Palette"][value="Let Us Choose"], input[data-option-name="Colour Palette"][value="Seasonal Mix"], input[data-option-name="Mood"][value="Seasonal Mix"]'
    );
    if (choice && !choice.checked && choice.dataset.optionAvailable !== 'false') choice.click();
  });
}
